import React, { useState, useEffect, useCallback } from 'react';

const API_BASE = process.env.NODE_ENV === 'production'
  ? '/api'
  : 'http://localhost:4001/api';

function Admin({ sessionToken, onBack }) {
  const [users, setUsers] = useState([]);
  const [connectedEmails, setConnectedEmails] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newUser, setNewUser] = useState({
    full_name: '',
    user_name: '',
    user_password: '',
    active: true,
    subscription_type: '',
    subscription_amount: '',
    referral_source: '',
    expiry_date: ''
  });

  const fetchConnectedUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/admin/connected-users`, {
        headers: { 'x-session-token': sessionToken }
      });
      if (response.ok) {
        const data = await response.json();
        setConnectedEmails(new Set(data.map(e => e.toLowerCase())));
      }
    } catch (err) {
      // silently fail
    }
  }, [sessionToken]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/admin/users`, {
        headers: { 'x-session-token': sessionToken }
      });

      if (!response.ok) {
        if (response.status === 403) {
          setError('Admin access required');
        } else {
          setError('Failed to fetch users');
        }
        return;
      }

      const data = await response.json();
      setUsers(data);
      setError('');
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  }, [sessionToken]);

  useEffect(() => {
    fetchUsers();
    fetchConnectedUsers();
    const interval = setInterval(fetchConnectedUsers, 10000);
    return () => clearInterval(interval);
  }, [fetchUsers, fetchConnectedUsers]);

  const handleToggleActive = async (userId) => {
    try {
      const response = await fetch(`${API_BASE}/admin/users/${userId}/toggle`, {
        method: 'PATCH',
        headers: { 'x-session-token': sessionToken }
      });

      if (response.ok) {
        fetchUsers();
      }
    } catch (err) {
      setError('Failed to update user');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete "${userName}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'x-session-token': sessionToken }
      });

      if (response.ok) {
        fetchUsers();
      }
    } catch (err) {
      setError('Failed to delete user');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(`${API_BASE}/admin/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-session-token': sessionToken
        },
        body: JSON.stringify({
          ...newUser,
          subscription_amount: newUser.subscription_amount ? parseFloat(newUser.subscription_amount) : null,
          subscription_type: newUser.subscription_type || null,
          referral_source: newUser.referral_source || null,
          expiry_date: newUser.expiry_date || null
        })
      });

      if (response.ok) {
        setShowAddModal(false);
        setNewUser({
          full_name: '',
          user_name: '',
          user_password: '',
          active: true,
          subscription_type: '',
          subscription_amount: '',
          referral_source: '',
          expiry_date: ''
        });
        fetchUsers();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to add user');
      }
    } catch (err) {
      setError('Failed to add user');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(`${API_BASE}/admin/users/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-session-token': sessionToken
        },
        body: JSON.stringify({
          ...editingUser,
          subscription_amount: editingUser.subscription_amount ? parseFloat(editingUser.subscription_amount) : null,
          subscription_type: editingUser.subscription_type || null,
          referral_source: editingUser.referral_source || null,
          expiry_date: editingUser.expiry_date || null
        })
      });

      if (response.ok) {
        setEditingUser(null);
        fetchUsers();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to update user');
      }
    } catch (err) {
      setError('Failed to update user');
    }
  };

  const formatDate = (date) => {
    if (!date) return '--';
    return new Date(date).toLocaleDateString();
  };

  if (loading) {
    return <div style={styles.container}><div style={styles.loading}>Loading...</div></div>;
  }

  if (error === 'Admin access required') {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You do not have admin privileges.</p>
          <button onClick={onBack} style={styles.backButton}>Go Back</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <button onClick={onBack} style={styles.backButton}>← Back to Dashboard</button>
          <h1 style={styles.title}>User Administration</h1>
        </div>
        <button onClick={() => setShowAddModal(true)} style={styles.addButton}>
          + Add User
        </button>
      </header>

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, width: '40px', textAlign: 'center' }}></th>
              <th style={styles.th}>ID</th>
              <th style={styles.th}>Full Name</th>
              <th style={styles.th}>Username</th>
              <th style={styles.th}>Active</th>
              <th style={styles.th}>Subscription</th>
              <th style={styles.th}>Amount</th>
              <th style={styles.th}>Created</th>
              <th style={styles.th}>Last Login</th>
              <th style={styles.th}>Referral</th>
              <th style={styles.th}>Expiry</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id} style={styles.tr}>
                <td style={{ ...styles.td, textAlign: 'center' }}>
                  <span style={{
                    display: 'inline-block',
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: connectedEmails.has((user.user_name || '').toLowerCase()) ? '#3fb950' : '#6e3630'
                  }} title={connectedEmails.has((user.user_name || '').toLowerCase()) ? 'Online' : 'Offline'} />
                </td>
                <td style={styles.td}>{user.id}</td>
                <td style={styles.td}>{user.full_name || '--'}</td>
                <td style={styles.td}>{user.user_name}</td>
                <td style={styles.td}>
                  <button
                    onClick={() => handleToggleActive(user.id)}
                    style={{
                      ...styles.statusButton,
                      backgroundColor: user.active ? '#238636' : '#6e3630'
                    }}
                  >
                    {user.active ? 'Active' : 'Disabled'}
                  </button>
                </td>
                <td style={styles.td}>{user.subscription_type || '--'}</td>
                <td style={styles.td}>{user.subscription_amount ? `$${user.subscription_amount}` : '--'}</td>
                <td style={styles.td}>{formatDate(user.date_created)}</td>
                <td style={styles.td}>{formatDate(user.activated_date)}</td>
                <td style={styles.td}>{user.referral_source || '--'}</td>
                <td style={styles.td}>{formatDate(user.expiry_date)}</td>
                <td style={styles.td}>
                  <div style={styles.actionButtons}>
                    <button
                      onClick={() => setEditingUser({ ...user })}
                      style={styles.editButton}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteUser(user.id, user.user_name)}
                      style={styles.deleteButton}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>Add New User</h2>
            <form onSubmit={handleAddUser} style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Full Name</label>
                <input
                  type="text"
                  value={newUser.full_name}
                  onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                  style={styles.input}
                  placeholder="John Smith"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Username / Email *</label>
                <input
                  type="text"
                  value={newUser.user_name}
                  onChange={(e) => setNewUser({ ...newUser, user_name: e.target.value })}
                  style={styles.input}
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Password *</label>
                <input
                  type="text"
                  value={newUser.user_password}
                  onChange={(e) => setNewUser({ ...newUser, user_password: e.target.value })}
                  style={styles.input}
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Active</label>
                <select
                  value={newUser.active}
                  onChange={(e) => setNewUser({ ...newUser, active: e.target.value === 'true' })}
                  style={styles.input}
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Subscription Type</label>
                <select
                  value={newUser.subscription_type}
                  onChange={(e) => setNewUser({ ...newUser, subscription_type: e.target.value })}
                  style={styles.input}
                >
                  <option value="">None</option>
                  <option value="Free">Free</option>
                  <option value="Eval">Eval</option>
                  <option value="Monthly">Monthly</option>
                  <option value="Yearly">Yearly</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Subscription Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={newUser.subscription_amount}
                  onChange={(e) => setNewUser({ ...newUser, subscription_amount: e.target.value })}
                  style={styles.input}
                  placeholder="0.00"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Referral Source</label>
                <input
                  type="text"
                  value={newUser.referral_source}
                  onChange={(e) => setNewUser({ ...newUser, referral_source: e.target.value })}
                  style={styles.input}
                  placeholder="e.g., Google, Friend, Twitter"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Expiry Date</label>
                <input
                  type="date"
                  value={newUser.expiry_date ? newUser.expiry_date.split('T')[0] : ''}
                  onChange={(e) => setNewUser({ ...newUser, expiry_date: e.target.value })}
                  style={styles.input}
                />
              </div>
              <div style={styles.modalActions}>
                <button type="button" onClick={() => setShowAddModal(false)} style={styles.cancelButton}>
                  Cancel
                </button>
                <button type="submit" style={styles.submitButton}>
                  Add User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>Edit User</h2>
            <form onSubmit={handleUpdateUser} style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Full Name</label>
                <input
                  type="text"
                  value={editingUser.full_name || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, full_name: e.target.value })}
                  style={styles.input}
                  placeholder="John Smith"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Username / Email</label>
                <input
                  type="text"
                  value={editingUser.user_name}
                  onChange={(e) => setEditingUser({ ...editingUser, user_name: e.target.value })}
                  style={styles.input}
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Password</label>
                <input
                  type="text"
                  value={editingUser.user_password || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, user_password: e.target.value })}
                  style={styles.input}
                  placeholder="Leave blank to keep current"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Active</label>
                <select
                  value={editingUser.active}
                  onChange={(e) => setEditingUser({ ...editingUser, active: e.target.value === 'true' })}
                  style={styles.input}
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Subscription Type</label>
                <select
                  value={editingUser.subscription_type || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, subscription_type: e.target.value })}
                  style={styles.input}
                >
                  <option value="">None</option>
                  <option value="Free">Free</option>
                  <option value="Eval">Eval</option>
                  <option value="Monthly">Monthly</option>
                  <option value="Yearly">Yearly</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Subscription Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={editingUser.subscription_amount || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, subscription_amount: e.target.value })}
                  style={styles.input}
                  placeholder="0.00"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Referral Source</label>
                <input
                  type="text"
                  value={editingUser.referral_source || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, referral_source: e.target.value })}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Expiry Date</label>
                <input
                  type="date"
                  value={editingUser.expiry_date ? editingUser.expiry_date.split('T')[0] : ''}
                  onChange={(e) => setEditingUser({ ...editingUser, expiry_date: e.target.value })}
                  style={styles.input}
                />
              </div>
              <div style={styles.modalActions}>
                <button type="button" onClick={() => setEditingUser(null)} style={styles.cancelButton}>
                  Cancel
                </button>
                <button type="submit" style={styles.submitButton}>
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#0d1117',
    color: '#e6e6e6',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    borderBottom: '1px solid #30363d',
    backgroundColor: '#161b22'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  },
  title: {
    margin: 0,
    fontSize: '20px',
    fontWeight: 600,
    color: '#ffffff'
  },
  backButton: {
    padding: '8px 16px',
    backgroundColor: '#21262d',
    color: '#c9d1d9',
    border: '1px solid #30363d',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  addButton: {
    padding: '10px 20px',
    backgroundColor: '#238636',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 600
  },
  loading: {
    textAlign: 'center',
    padding: '48px',
    color: '#8b949e',
    fontSize: '16px'
  },
  error: {
    margin: '16px 24px',
    padding: '12px 16px',
    backgroundColor: '#3d1a1a',
    border: '1px solid #6e3630',
    borderRadius: '6px',
    color: '#ff7b72'
  },
  accessDenied: {
    textAlign: 'center',
    padding: '48px',
    color: '#8b949e'
  },
  tableContainer: {
    padding: '24px',
    overflowX: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: '#161b22',
    borderRadius: '8px',
    overflow: 'hidden'
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    borderBottom: '1px solid #30363d',
    backgroundColor: '#21262d',
    color: '#8b949e',
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  tr: {
    borderBottom: '1px solid #30363d'
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#c9d1d9'
  },
  statusButton: {
    padding: '4px 12px',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 600,
    color: '#ffffff'
  },
  actionButtons: {
    display: 'flex',
    gap: '8px'
  },
  editButton: {
    padding: '6px 12px',
    backgroundColor: '#1f6feb',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px'
  },
  deleteButton: {
    padding: '6px 12px',
    backgroundColor: '#da3633',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: '#161b22',
    borderRadius: '12px',
    border: '1px solid #30363d',
    padding: '24px',
    width: '100%',
    maxWidth: '480px',
    maxHeight: '90vh',
    overflowY: 'auto'
  },
  modalTitle: {
    margin: '0 0 24px 0',
    fontSize: '20px',
    fontWeight: 600,
    color: '#ffffff'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  label: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#c9d1d9'
  },
  input: {
    padding: '10px 12px',
    backgroundColor: '#0d1117',
    border: '1px solid #30363d',
    borderRadius: '6px',
    color: '#e6e6e6',
    fontSize: '14px',
    outline: 'none'
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '8px'
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: '#21262d',
    color: '#c9d1d9',
    border: '1px solid #30363d',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  submitButton: {
    padding: '10px 20px',
    backgroundColor: '#238636',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 600
  }
};

export default Admin;
